import useStyles from "./styles";
import { CustomModal } from "../../../../components/CustomModal/CustomModal";
import { useTranslation } from "react-i18next";
import { CustomTextInput } from "../../../../components/CustomTextInput/CustomTextInput";
import { CustomTextSelect } from "../../../../components/CustomTextSelect/CustomTextSelect";
import { FC, ReactNode, useEffect, useMemo, useRef, useState } from "react";
import { CustomSearch } from "../../../../components/CustomSearch/CustomSearch";
import { useGetAllBrands, useGetAllModel } from "../../../../api/brandModel";
import { CustomTextDatePicker } from "../../../../components/CustomTextDatePicker/CustomTextDatePicker";
import { getVehicleStatusOptions } from "../../../../utils/types";
import { useFormik } from "formik";
import dayjs from "dayjs";
import { SelectChangeEvent } from "@mui/material";
import { ModalLeasing } from "../../CreateVehicule/StepCreateFileVehicule/ModalLeasing/ModalLeasing";
import toast from "react-hot-toast";
import { getCurrentFleetId } from "../../../../utils/getCurrentFleet";
import { useParams } from "react-router";
import {
  useGetOneVehicle,
  useUpdateVehicule,
  useUpdateVehiculeFleet,
} from "../../../../api/vehicule";
import {
  FormValuesVehicleUpdate,
  initialValuesVehicleUpdate,
  validationSchemaUpdateVehicle,
} from "./validationShemaUpdate";
import {
  fieldError,
  missingFieldsMessage,
  touchAll,
} from "../../vehicleFormErrors";
import nextId from "react-id-generator";

type props = {
  opeModal: boolean;
  onClose: () => void;
  onCancel: () => void;
};

/** The API sends numbers for some of these columns; the form works on text. */
const asText = (value: unknown): string =>
  value === null || value === undefined ? "" : String(value);

export const ModalUpdateVehicle: FC<props> = ({
  opeModal,
  onCancel,
  onClose,
}) => {
  const classes = useStyles();
  const { t } = useTranslation();

  // Translated labels, code values. CustomTextSelect already supports
  // { label, value } options, so nothing here has to parse a display string.
  const statusOptions = useMemo(() => getVehicleStatusOptions(t), [t]);

  const { id = "" } = useParams();
  const vehicleId = parseInt(id);

  const { vehicule } = useGetOneVehicle(getCurrentFleetId(), vehicleId);
  const { brands } = useGetAllBrands();

  const [brand, setbrand] = useState<TBrand | null>(null);
  const { brandsModel } = useGetAllModel(brand?.id ?? null);

  const [keyModel, setKeyModel] = useState(nextId());

  // The VIN stays read-only once known (it identifies the vehicle), but a
  // vehicle that has none must be completable: the field used to be disabled
  // AND required, so such a vehicle could never be saved again.
  const hasStoredVin = asText(vehicule?.vin).trim().length > 0;

  useEffect(() => {
    if (vehicule) {
      formik.setFieldValue("vin", asText(vehicule.vin));
      formik.setFieldValue("immatriculation", asText(vehicule.immatriculation));
      // brand - extract from brandModel object. The API always sends the
      // brandModel OBJECT, with null ids when the vehicle has no model yet:
      // an object with a null id is "no brand", not a brand.
      const brandData = typeof vehicule.brandModel === 'object' && vehicule.brandModel?.brand
        ? vehicule.brandModel.brand
        : { id: vehicule.brandId, name: vehicule.brand };
      const currentbrand = brandData?.id
        ? { id: brandData.id, name: brandData.name }
        : null;
      setbrand(currentbrand as TBrand | null);
      formik.setFieldValue("brand", currentbrand);
      const currentModel =
        typeof vehicule.brandModel === "object" && (vehicule.brandModel as any)?.id
          ? vehicule.brandModel
          : null;
      formik.setFieldValue("modele", currentModel);

      formik.setFieldValue("gearBox", asText(vehicule.boite));
      // puissance can come back as a NUMBER: the old checkValues() called
      // .trim() on it, threw, and Formik swallowed the exception - Save did
      // nothing at all.
      formik.setFieldValue("fiscalPower", asText(vehicule.puissance));
      formik.setFieldValue("energy", asText(vehicule.carburant));
      // A stored date, or "" - never dayjs(null), whose .format() is the
      // string "Invalid Date" that used to be sent to the API.
      const release = dayjs(vehicule.mise_en_circulation);
      formik.setFieldValue(
        "releaseDate",
        vehicule.mise_en_circulation && release.isValid()
          ? release.format("YYYY-MM-DD")
          : ""
      );

      const vehicleStatus = (vehicule as any).fleetVehicleStatus ?? vehicule.fleetVehicleStatut;
      // The code goes straight into the form. It used to be converted to the
      // French enum key here and converted back on submit, which is how a UI
      // string ended up as the transport format for a status.
      formik.setFieldValue("status", vehicleStatus ?? "F");
      // Re-opening the modal starts clean: no leftover error from last time.
      formik.setTouched({}, false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [vehicule, opeModal]);

  // The two writes are SEQUENTIAL and report once (see onSubmit), so the
  // hooks' own success toast is turned off here.
  const { mutateAsync: updateVehicle, isPending: isPendingVehicle } =
    useUpdateVehicule(getCurrentFleetId(), vehicleId, undefined, {
      notifySuccess: false,
    });
  const { mutateAsync: updateVehicleFleet, isPending: isPendingFleet } =
    useUpdateVehiculeFleet(getCurrentFleetId(), vehicleId, {
      notifySuccess: false,
    });
  const isSaving = isPendingVehicle || isPendingFleet;

  // Synchronous double-click guard: isPending only flips on the next render.
  const submittingRef = useRef(false);

  const [selectedRental, setSelectedRental] = useState({
    label: "",
    value: "",
  });
  const [openLeasing, setOpenLeasing] = useState(false);

  const formik = useFormik<FormValuesVehicleUpdate>({
    initialValues: initialValuesVehicleUpdate,
    validationSchema: validationSchemaUpdateVehicle,
    onSubmit: async (values) => {
      if (submittingRef.current) return;
      submittingRef.current = true;

      const vin = asText(values.vin).trim();
      const release = dayjs(values.releaseDate);

      try {
        await updateVehicle({
          // Only sent when there IS one: "" would collide with every other
          // VIN-less vehicle on the UNIQUE(vin) index.
          ...(vin ? { vin } : {}),
          number: asText(values.immatriculation).trim(),
          // owner_company_id:
          //   selectedRental.label.trim().length === 0
          //     ? getCurrentFleetId()
          //     : Number(selectedRental.value),
          brand_model_id: Number(values.modele?.id),
          // not required api
          boite: asText(values.gearBox),
          puissance: asText(values.fiscalPower).trim(),
          carburant: asText(values.energy),
          mise_en_circulation:
            values.releaseDate && release.isValid()
              ? release.format("YYYY-MM-DD")
              : null,
          register_step: 0,
        });
        // Only once the vehicle itself is saved, and with the cost the
        // vehicle ALREADY has. This modal has no cost field; it used to send
        // a hard-coded 0, so every edit silently wiped the monthly average
        // cost that feeds the TCO.
        await updateVehicleFleet({
          vehicle_status: values.status ?? "F",
          monthly_average_cost: Number(vehicule?.monthly_average_cost) || 0,
        });
        toast.success(t("common.changeSaved", "Change saved"));
        onClose && onClose();
      } catch {
        // The failing hook has already toasted the reason; the modal stays
        // open with everything the user typed.
      } finally {
        submittingRef.current = false;
      }
    },
  });

  const errorOf = (field: keyof FormValuesVehicleUpdate) =>
    fieldError(t, formik.errors, formik.touched, field);

  // const handleRadioChange = (
  //   _: ChangeEvent<HTMLInputElement>,
  //   value: string
  // ) => {
  //   formik.setFieldValue("isLeasing", value);
  //   if (value === "yes") {
  //     setOpenLeasing(true);
  //   } else {
  //     setSelectedRental({ label: "", value: "" });
  //     setOpenLeasing(false);
  //   }
  // };

  const onChangeAutoCompletBrand = (_: any, brand: any) => {
    const picked = brand && typeof brand === "object" && brand.id ? brand : null;
    setbrand(picked as TBrand | null);
    formik?.setFieldValue("brand", picked);
    formik?.setFieldValue("modele", null);
    setKeyModel(nextId());
  };

  const onChangeAutoCompletModele = (_: any, modele: any) => {
    const picked = modele && typeof modele === "object" && modele.id ? modele : null;
    formik?.setFieldValue("modele", picked);
  };

  const onSaveLeasing = () => {
    if (selectedRental?.label?.trim()?.length > 0) {
      setOpenLeasing(false);
    } else {
      toast.error(
        t("required.selectRentalCompany", "Please select a rental company")
      );
    }
  };

  const onCloseLeasing = () => {
    setOpenLeasing(false);
    formik?.setFieldValue("isLeasing", null);
    setSelectedRental({ label: "", value: "" });
  };

  const onChangeSelectRentalCompany = (
    event: SelectChangeEvent<any>,
    _: ReactNode
  ) => {
    setSelectedRental(event.target.value as { label: string; value: string });
  };

  const fieldLabels = {
    immatriculation: t("vehicule.registration", "Registration"),
    brand: t("vehicule.brand", "Brand"),
    modele: t("vehicule.model", "Model"),
    gearBox: t("vehicule.gearBox", "Gearbox"),
    fiscalPower: t("vehicule.fiscalePower", "Fiscal power"),
    energy: t("vehicule.fuel", "Fuel"),
    releaseDate: t("vehicule.release", "Release"),
    status: t("vehicule.status", "Status"),
  };

  /**
   * Formik refuses to call onSubmit while the form is invalid - and nothing
   * on screen said so. Validate first, and when something blocks, show it
   * under the field and in one toast.
   */
  const handleSave = async () => {
    if (isSaving || submittingRef.current) return;
    const errors = await formik.validateForm();
    if (Object.keys(errors).length > 0) {
      formik.setTouched(touchAll(formik.values), false);
      toast.error(missingFieldsMessage(t, errors, fieldLabels));
      return;
    }
    await formik.submitForm();
  };

  return (
    <>
      <ModalLeasing
        open={openLeasing}
        title={t("vehicule.societyName", "Adding rental company name")}
        valueSelectedRental={selectedRental}
        onSave={onSaveLeasing}
        onCancel={onCloseLeasing}
        onClose={onCloseLeasing}
        onChange={onChangeSelectRentalCompany}
      />

      <CustomModal
        width={"663px"}
        open={opeModal}
        onCancel={onCancel}
        title={t("vehicule.editVehicle", "Edit vehicle")}
        onClose={onClose}
        onSave={handleSave}
        loadingSave={isSaving}
      >
        <div className={classes.containerText}>
          <div className={classes.divModalText}>
            <CustomTextInput
              name="vin"
              value={formik?.values?.vin ?? ""}
              onChange={formik.handleChange}
              label={t("vehicule.vin", "VIN")}
              placeholder={t("vehicule.vin", "VIN")}
              disabled={hasStoredVin}
              helperTextError={errorOf("vin")}
            />
            <CustomTextInput
              name="immatriculation"
              value={formik?.values?.immatriculation ?? ""}
              onChange={formik.handleChange}
              onBlur={formik.handleBlur}
              label={`${t("vehicule.registration", "registration")} *`}
              placeholder={t("vehicule.registration", "Registration")}
              helperTextError={errorOf("immatriculation")}
            />
          </div>
          <div className={classes.divModalText}>
            <CustomSearch
              options={brands}
              getOptionLabel={(option: any) => option?.name}
              label={`${t("vehicule.brand", "Brand")} *`}
              placeholder={t("vehicule.brand", "Brand")}
              strictOptions
              value={formik?.values?.brand}
              onChange={onChangeAutoCompletBrand}
              helperTextError={errorOf("brand")}
              onChangeTextField={(event) => {
                if (event.target.value?.trim().length === 0) {
                  setbrand(null);
                  setKeyModel(nextId());
                  formik?.setFieldValue("brand", null);
                  formik?.setFieldValue("modele", null);
                }
              }}
            />
            <CustomSearch
              key={keyModel}
              options={brandsModel}
              getOptionLabel={(option: any) => option?.name}
              label={`${t("vehicule.model", "Model")} *`}
              placeholder={t("vehicule.model", "Model")}
              strictOptions
              value={formik?.values?.modele}
              onChange={onChangeAutoCompletModele}
              helperTextError={errorOf("modele")}
              onChangeTextField={(event) => {
                if (event.target.value?.trim().length === 0) {
                  formik?.setFieldValue("modele", null);
                }
              }}
            />
          </div>
          <div className={classes.divModalText}>
            <CustomTextSelect
              label={`${t("vehicule.gearBox", "Gearbox")} *`}
              options={["Automatique", "Manuelle"]}
              placeholder={t("vehicule.gearBox", "Gearbox")}
              name="gearBox"
              value={formik?.values?.gearBox}
              onChange={formik.handleChange}
              helperTextError={errorOf("gearBox")}
            />
            <CustomTextInput
              label={`${t("vehicule.fiscalePower", "Fiscal power")} *`}
              placeholder={t("vehicule.fiscalePower", "Fiscal power")}
              name="fiscalPower"
              value={formik?.values?.fiscalPower ?? ""}
              onChange={formik.handleChange}
              onBlur={formik.handleBlur}
              helperTextError={errorOf("fiscalPower")}
            />
          </div>
          <div className={classes.divModalText}>
            <CustomTextSelect
              label={`${t("vehicule.fuel", "Fuel")} *`}
              options={[
                "Essence",
                "Diesel",
                "Electrique",
                "GPL",
                "Hydrogène",
                "Ethanol",
                // Same list as the creation step: a hybrid created there
                // used to show an empty Fuel field here.
                "Hybride",
                "hybride rechargeable",
              ]}
              placeholder={t("vehicule.fuel", "Fuel")}
              name="energy"
              value={formik?.values?.energy}
              onChange={formik.handleChange}
              helperTextError={errorOf("energy")}
            />
            <CustomTextDatePicker
              label={`${t("vehicule.release", "Release")} *`}
              value={formik?.values?.releaseDate || null}
              helperTextError={errorOf("releaseDate")}
              onChange={(value) =>
                formik?.setFieldValue(
                  "releaseDate",
                  value && dayjs(value).isValid()
                    ? dayjs(value).format("YYYY-MM-DD")
                    : ""
                )
              }
            />
          </div>
          <div className={classes.divModalText}>
            <CustomTextSelect
              label={`${t("vehicule.status", "Status")} *`}
              options={statusOptions}
              placeholder={t("vehicule.status", "Status")}
              name="status"
              value={formik?.values?.status}
              onChange={formik.handleChange}
              helperTextError={errorOf("status")}
            />
          </div>
          {/* <div className={classes.divRent}>
            <CustomTypography
              text={t("vehicule.rent", "Is this a rental or leasing car? *")}
              variant={"link"}
            />
            <FormControl>
              <RadioGroup
                row
                value={formik?.values?.isLeasing}
                name="isLeasing"
                onChange={(event) => {
                  handleRadioChange(event, event.target.value);
                }}
              >
                <FormControlLabel
                  value={"no"}
                  control={<Radio />}
                  label={t("common.no", "No")}
                />
                <FormControlLabel
                  value={"yes"}
                  control={<Radio />}
                  label={t("common.yes", "Yes")}
                />
              </RadioGroup>
            </FormControl>
          </div> */}
        </div>
      </CustomModal>
    </>
  );
};
